// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - SUPABASE MANAGER
// All database read/write operations
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabaseClient';

// ═══════════════════════════════════════════════════════════════════════════
// COIN METADATA — decimals + icon cache backed by Supabase
// Prevents wrong decimal values from causing incorrect token counts + prices
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch decimals + iconUrl for a list of coin types in one Supabase query.
 * Returns { coinType: { decimals, iconUrl, rpcVerified } }
 */
export async function getDecimalsFromSupabase(coinTypes) {
  if (!coinTypes || coinTypes.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('coin_metadata')
      .select('coin_type, decimals, icon_url, rpc_verified')
      .in('coin_type', coinTypes);

    if (error) {
      console.warn('⚠️ getDecimalsFromSupabase error:', error.message);
      return {};
    }

    const result = {};
    (data || []).forEach(row => {
      result[row.coin_type] = {
        decimals: row.decimals,
        iconUrl: row.icon_url || null,
        rpcVerified: row.rpc_verified || false,
      };
    });
    return result;
  } catch (err) {
    console.warn('⚠️ getDecimalsFromSupabase failed:', err.message);
    return {};
  }
}

/**
 * Upsert verified coin metadata entries into Supabase.
 * Called by background verify after RPC confirmation.
 * entries: [{ coinType, decimals, symbol, iconUrl, rpcVerified }]
 */
export async function upsertCoinMetadata(entries) {
  if (!entries || entries.length === 0) return;
  try {
    const rows = entries.map(e => ({
      coin_type: e.coinType,
      decimals: e.decimals,
      symbol: e.symbol || null,
      icon_url: e.iconUrl || null,
      rpc_verified: e.rpcVerified ?? false,
      last_verified: e.rpcVerified ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }));

    const { error } = await supabase
      .from('coin_metadata')
      .upsert(rows, { onConflict: 'coin_type' });

    if (error) {
      console.warn('⚠️ upsertCoinMetadata error:', error.message);
    }
  } catch (err) {
    console.warn('⚠️ upsertCoinMetadata failed:', err.message);
  }
}


// Called after every successful real wallet purification
// ═══════════════════════════════════════════════════════════════════════════

export async function recordPurification({
  walletAddress,
  objectCount,
  purityEarned,
  suiReclaimed,
  gasUsed,
  storageRebateMist,
  transactionHash,
  categoryBreakdown,
  isToiletMode = false,
}) {
  try {
    const breakdown = categoryBreakdown || {};

    // 1. Insert purification record
    const { error: insertError } = await supabase
      .from('purifications')
      .insert({
        wallet_address: walletAddress,
        object_count: objectCount || 0,
        purity_earned: purityEarned || 0,
        sui_reclaimed: suiReclaimed || 0,
        gas_used: gasUsed || 0,
        storage_rebate_mist: storageRebateMist || 0,
        transaction_hash: transactionHash || null,
        category_toxic: breakdown.TOXIC || 0,
        category_spam: breakdown.SPAM || 0,
        category_dust: breakdown.DUST || 0,
        is_toilet_mode: isToiletMode,
      });

    if (insertError) {
      console.error('Supabase insert error:', insertError);
      return false;
    }

    // 2. Upsert user_stats (update aggregated totals)
    const { data: existing } = await supabase
      .from('user_stats')
      .select('*')
      .eq('wallet_address', walletAddress)
      .single();

    if (existing) {
      const { error: updateError } = await supabase
        .from('user_stats')
        .update({
          total_purifications: existing.total_purifications + 1,
          total_objects_cleaned: existing.total_objects_cleaned + (objectCount || 0),
          total_purity_earned: existing.total_purity_earned + (purityEarned || 0),
          total_sui_reclaimed: existing.total_sui_reclaimed + (suiReclaimed || 0),
          total_toilet_purifications: existing.total_toilet_purifications + (isToiletMode ? 1 : 0),
          last_purification: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('wallet_address', walletAddress);

      if (updateError) console.error('Supabase update user_stats error:', updateError);
    } else {
      const { error: upsertError } = await supabase
        .from('user_stats')
        .insert({
          wallet_address: walletAddress,
          total_purifications: 1,
          total_objects_cleaned: objectCount || 0,
          total_purity_earned: purityEarned || 0,
          total_sui_reclaimed: suiReclaimed || 0,
          total_toilet_purifications: isToiletMode ? 1 : 0,
          last_purification: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (upsertError) console.error('Supabase insert user_stats error:', upsertError);
    }

    return true;
  } catch (err) {
    console.error('Supabase recordPurification failed:', err);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Leaderboard - top purifiers by purity earned
// ═══════════════════════════════════════════════════════════════════════════

export async function getLeaderboard(limit = 10) {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('wallet_address, total_purity_earned, total_purifications, total_objects_cleaned, last_purification')
      .order('total_purity_earned', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Supabase leaderboard error:', error);
      return [];
    }

    const addresses = (data || []).map(r => r.wallet_address);
    const usernameMap = await getUsernamesForAddresses(addresses);

    return (data || []).map((row, index) => {
      const username = usernameMap[row.wallet_address];
      return {
        rank: index + 1,
        address: row.wallet_address,
        displayName: username || shortenAddress(row.wallet_address),
        hasUsername: !!username,
        totalPurity: row.total_purity_earned,
        purifications: row.total_purifications,
        objectsCleaned: row.total_objects_cleaned,
        lastPurification: row.last_purification,
      };
    });
  } catch (err) {
    console.error('Supabase getLeaderboard failed:', err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Protocol-wide totals for landing page + stats overview
// ═══════════════════════════════════════════════════════════════════════════

export async function getProtocolTotals() {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('total_purifications, total_objects_cleaned, total_purity_earned, total_sui_reclaimed');

    if (error) {
      console.error('Supabase protocol totals error:', error);
      return null;
    }

    const totals = (data || []).reduce(
      (acc, row) => ({
        totalPurifications: acc.totalPurifications + (row.total_purifications || 0),
        totalObjectsCleaned: acc.totalObjectsCleaned + (row.total_objects_cleaned || 0),
        totalPurityEarned: acc.totalPurityEarned + (row.total_purity_earned || 0),
        totalSuiReclaimed: acc.totalSuiReclaimed + (row.total_sui_reclaimed || 0),
        activePurifiers: acc.activePurifiers + 1,
      }),
      {
        totalPurifications: 0,
        totalObjectsCleaned: 0,
        totalPurityEarned: 0,
        totalSuiReclaimed: 0,
        activePurifiers: 0,
      }
    );

    return totals;
  } catch (err) {
    console.error('Supabase getProtocolTotals failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Recent activity feed (last N purifications across all wallets)
// ═══════════════════════════════════════════════════════════════════════════

export async function getRecentActivity(limit = 30) {
  try {
    const { data, error } = await supabase
      .from('purifications')
      .select('wallet_address, object_count, purity_earned, sui_reclaimed, transaction_hash, is_toilet_mode, game_type, game_score, created_at')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Supabase activity error:', error);
      return [];
    }

    return (data || []).map(row => ({
      address: shortenAddress(row.wallet_address),
      objectCount: row.object_count,
      purityEarned: row.purity_earned,
      suiReclaimed: row.sui_reclaimed,
      transactionHash: row.transaction_hash,
      isToiletMode: row.is_toilet_mode,
      gameType: row.game_type || null,
      gameScore: row.game_score || null,
      timestamp: row.created_at,
    }));
  } catch (err) {
    console.error('Supabase getRecentActivity failed:', err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Daily activity for chart (last N days)
// ═══════════════════════════════════════════════════════════════════════════

export async function getDailyActivityFromDB(days = 30) {
  try {
    const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('purifications')
      .select('object_count, purity_earned, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Supabase daily activity error:', error);
      return [];
    }

    // Group by date
    const byDate = {};
    (data || []).forEach(row => {
      const date = row.created_at.split('T')[0];
      if (!byDate[date]) {
        byDate[date] = { date, purifications: 0, objectsCleaned: 0, purityEarned: 0 };
      }
      byDate[date].purifications += 1;
      byDate[date].objectsCleaned += row.object_count || 0;
      byDate[date].purityEarned += row.purity_earned || 0;
    });

    // Fill in missing days with zeros
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      result.push(byDate[date] || { date, purifications: 0, objectsCleaned: 0, purityEarned: 0 });
    }

    return result;
  } catch (err) {
    console.error('Supabase getDailyActivity failed:', err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Get a specific wallet's rank
// ═══════════════════════════════════════════════════════════════════════════

export async function getWalletRank(walletAddress) {
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('wallet_address, total_purity_earned')
      .order('total_purity_earned', { ascending: false });

    if (error || !data) return null;

    const rank = data.findIndex(r => r.wallet_address === walletAddress) + 1;
    return rank > 0 ? { rank, total: data.length } : { rank: data.length + 1, total: data.length };
  } catch (err) {
    console.error('Supabase getWalletRank failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// USERNAME SYSTEM
// ═══════════════════════════════════════════════════════════════════════════

export async function setUsername(walletAddress, username) {
  // ── Validation ────────────────────────────────────────────────────────────
  if (!username || typeof username !== 'string') {
    return { success: false, error: 'Invalid username' };
  }
  const trimmed = username.trim();
  if (trimmed.length < 3 || trimmed.length > 20) {
    return { success: false, error: 'Username must be 3–20 characters' };
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return { success: false, error: 'Letters, numbers, underscores and hyphens only' };
  }
  const RESERVED = ['admin', 'distil', 'protocol', 'toilet', 'root', 'system'];
  if (RESERVED.includes(trimmed.toLowerCase())) {
    return { success: false, error: 'That username is reserved' };
  }

  try {
    // Check if username is taken
    const { data: existing } = await supabase
      .from('usernames')
      .select('wallet_address')
      .eq('username', trimmed)
      .single();

    if (existing && existing.wallet_address !== walletAddress) {
      return { success: false, error: 'Username already taken' };
    }

    const { error } = await supabase
      .from('usernames')
      .upsert({ wallet_address: walletAddress, username: trimmed }, { onConflict: 'wallet_address' });

    if (error) return { success: false, error: error.message };
    return { success: true };
  } catch (err) {
    return { success: false, error: 'Failed to set username' };
  }
}

export async function getUsername(walletAddress) {
  try {
    const { data } = await supabase
      .from('usernames')
      .select('username')
      .eq('wallet_address', walletAddress)
      .single();
    return data?.username || null;
  } catch {
    return null;
  }
}

export async function getUsernamesForAddresses(addresses) {
  if (!addresses || addresses.length === 0) return {};
  try {
    const { data } = await supabase
      .from('usernames')
      .select('wallet_address, username')
      .in('wallet_address', addresses);
    const map = {};
    (data || []).forEach(row => { map[row.wallet_address] = row.username; });
    return map;
  } catch {
    return {};
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPER
// ═══════════════════════════════════════════════════════════════════════════

function shortenAddress(address) {
  if (!address) return 'Unknown';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// WRITE: Record a mini-game reward (FlushFrenzy, ScratchOff, SpinWheel)
// Adds purity to user_stats without inserting a full purification record
// ═══════════════════════════════════════════════════════════════════════════

export async function recordGameReward({
  walletAddress,
  purityEarned,
  gameType, // 'flush_frenzy' | 'scratch_off' | 'spin_wheel'
  gameScore = null,
  isToiletMode = false,
}) {
  if (!walletAddress || !purityEarned || purityEarned <= 0) return false;
  try {
    // 1. Insert into purifications table with game_type marker (no tx hash)
    const { error: insertError } = await supabase
      .from('purifications')
      .insert({
        wallet_address: walletAddress,
        object_count: 0,
        purity_earned: purityEarned,
        sui_reclaimed: 0,
        gas_used: 0,
        storage_rebate_mist: 0,
        transaction_hash: null,
        category_toxic: 0,
        category_spam: 0,
        category_dust: 0,
        is_toilet_mode: isToiletMode,
        game_type: gameType,
        game_score: gameScore,
      });

    if (insertError) {
      // If game_type column doesn't exist yet, fall through to stats update
      // (non-blocking — stats update is what matters for leaderboard/TGE)
      if (!insertError.message?.includes('game_type')) {
        console.error('Supabase game reward insert error:', insertError);
      }
    }

    // 2. Upsert user_stats — this is what drives leaderboard + TGE
    const { data: existing } = await supabase
      .from('user_stats')
      .select('total_purity_earned, total_toilet_purifications')
      .eq('wallet_address', walletAddress)
      .single();

    if (existing) {
      const { error: updateError } = await supabase
        .from('user_stats')
        .update({
          total_purity_earned: existing.total_purity_earned + purityEarned,
          total_toilet_purifications: existing.total_toilet_purifications + (isToiletMode ? 1 : 0),
          last_purification: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('wallet_address', walletAddress);

      if (updateError) console.error('Supabase game reward stats update error:', updateError);
    } else {
      // First ever interaction was a game — create the row
      const { error: insertStatsError } = await supabase
        .from('user_stats')
        .insert({
          wallet_address: walletAddress,
          total_purifications: 0,
          total_objects_cleaned: 0,
          total_purity_earned: purityEarned,
          total_sui_reclaimed: 0,
          total_toilet_purifications: isToiletMode ? 1 : 0,
          last_purification: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (insertStatsError) console.error('Supabase game reward new user_stats error:', insertStatsError);
    }

    return true;
  } catch (err) {
    console.error('Supabase recordGameReward failed:', err);
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ+WRITE: Sync wallet purity score — Supabase wins if higher than local
// Call on wallet connect to recover from cleared localStorage
// ═══════════════════════════════════════════════════════════════════════════

export async function syncWalletPurityScore(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data } = await supabase
      .from('user_stats')
      .select('total_purity_earned')
      .eq('wallet_address', walletAddress)
      .single();

    return data?.total_purity_earned ?? null;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Per-wallet lookup (Admin Panel tool)
// Returns full stats for a specific wallet address
// ═══════════════════════════════════════════════════════════════════════════

export async function getWalletStats(walletAddress) {
  if (!walletAddress) return null;
  try {
    const [statsRes, usernameRes, gameRes] = await Promise.all([
      supabase
        .from('user_stats')
        .select('*')
        .eq('wallet_address', walletAddress)
        .single(),
      supabase
        .from('usernames')
        .select('username')
        .eq('wallet_address', walletAddress)
        .single(),
      supabase
        .from('purifications')
        .select('game_type, purity_earned, created_at')
        .eq('wallet_address', walletAddress)
        .not('game_type', 'is', null)
        .order('created_at', { ascending: false })
        .limit(20),
    ]);

    if (!statsRes.data) return null;

    const gameRows = gameRes.data || [];
    const gameRewardTotal = gameRows.reduce((sum, r) => sum + (r.purity_earned || 0), 0);
    const gameTypeCounts = gameRows.reduce((acc, r) => {
      acc[r.game_type] = (acc[r.game_type] || 0) + 1;
      return acc;
    }, {});

    return {
      walletAddress,
      username: usernameRes.data?.username || null,
      totalPurityEarned: statsRes.data.total_purity_earned || 0,
      totalPurifications: statsRes.data.total_purifications || 0,
      totalObjectsCleaned: statsRes.data.total_objects_cleaned || 0,
      totalSuiReclaimed: statsRes.data.total_sui_reclaimed || 0,
      totalToiletPurifications: statsRes.data.total_toilet_purifications || 0,
      lastPurification: statsRes.data.last_purification || null,
      gameRewardTotal,
      gameTypeCounts,
      recentGameRewards: gameRows.slice(0, 5),
    };
  } catch (err) {
    console.error('Supabase getWalletStats failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Protocol-wide burn + emission aggregates from Supabase
// Computed from purifications table — no extra columns needed
// ═══════════════════════════════════════════════════════════════════════════

export async function getProtocolBurnStats() {
  try {
    const TOTAL_SUPPLY = 100_000_000;
    const BURN_RATE = 0.01; // 1% of every reward is burned

    // Sum all purity earned across ALL purifications (including game rewards)
    const { data, error } = await supabase
      .from('purifications')
      .select('purity_earned, created_at');

    if (error || !data) return null;

    const totalEmitted = data.reduce((sum, r) => sum + (r.purity_earned || 0), 0);
    const totalBurned = totalEmitted * BURN_RATE;

    // Weekly + monthly burns
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const oneMonth = 30 * 24 * 60 * 60 * 1000;

    let weeklyBurned = 0;
    let monthlyBurned = 0;

    data.forEach(row => {
      const age = now - new Date(row.created_at).getTime();
      const burnedThisRow = (row.purity_earned || 0) * BURN_RATE;
      if (age <= oneWeek) weeklyBurned += burnedThisRow;
      if (age <= oneMonth) monthlyBurned += burnedThisRow;
    });

    return {
      totalBurned: Math.round(totalBurned),
      burnPercentage: (totalBurned / TOTAL_SUPPLY) * 100,
      weeklyBurned: Math.round(weeklyBurned),
      monthlyBurned: Math.round(monthlyBurned),
      source: 'supabase',
    };
  } catch (err) {
    console.error('Supabase getProtocolBurnStats failed:', err);
    return null;
  }
}

export async function getProtocolEmissionStats() {
  try {
    // Phase definitions (mirror emissionSchedule.js)
    const PHASES = [
      { name: 'Phase 1', max: 2_000_000, rate: 10 },
      { name: 'Phase 2', max: 4_000_000, rate: 9 },
      { name: 'Phase 3', max: 6_000_000, rate: 8 },
      { name: 'Phase 4', max: 8_000_000, rate: 7 },
      { name: 'Phase 5', max: 10_000_000, rate: 6 },
    ];
    const TOTAL_EMISSION_CAP = 10_000_000;

    const { data, error } = await supabase
      .from('purifications')
      .select('purity_earned, created_at')
      .order('created_at', { ascending: true });

    if (error || !data) return null;

    const totalEmitted = data.reduce((sum, r) => sum + (r.purity_earned || 0), 0);

    // Current phase
    let currentPhase = PHASES[PHASES.length - 1];
    let tokensToNext = 0;
    for (const phase of PHASES) {
      if (totalEmitted < phase.max) {
        currentPhase = phase;
        tokensToNext = phase.max - totalEmitted;
        break;
      }
    }

    // Daily rate estimate (last 7 days)
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const recentRows = data.filter(r => r.created_at >= sevenDaysAgo);
    const recentEmitted = recentRows.reduce((sum, r) => sum + (r.purity_earned || 0), 0);
    const dailyRate = recentEmitted / 7;
    const estimatedDays = dailyRate > 0 ? Math.round(tokensToNext / dailyRate) : null;

    return {
      totalEmitted: Math.round(totalEmitted),
      currentPhase,
      currentRate: currentPhase.rate,
      percentageEmitted: (totalEmitted / TOTAL_EMISSION_CAP) * 100,
      tokensToNext: Math.round(tokensToNext),
      estimatedDays,
      source: 'supabase',
    };
  } catch (err) {
    console.error('Supabase getProtocolEmissionStats failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN CONFIG — read/write protocol addresses + settings
// Stored in Supabase admin_config table. App reads on load.
// ═══════════════════════════════════════════════════════════════════════════

export async function getAdminConfig() {
  try {
    const { data, error } = await supabase
      .from('admin_config')
      .select('key, value');

    if (error) return {};

    const config = {};
    (data || []).forEach(row => { config[row.key] = row.value; });
    return config;
  } catch {
    return {};
  }
}

export async function setAdminConfigValue(key, value) {
  try {
    const { error } = await supabase
      .from('admin_config')
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });

    if (error) {
      console.error('Supabase setAdminConfigValue error:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('Supabase setAdminConfigValue failed:', err);
    return false;
  }
}

export async function deleteAdminConfigValue(key) {
  try {
    const { error } = await supabase
      .from('admin_config')
      .delete()
      .eq('key', key);

    if (error) return false;
    return true;
  } catch {
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ: Per-wallet purification history
// Fetches full history from Supabase purifications table
// Used by HistoryPanel as source of truth (survives localStorage clears)
// ═══════════════════════════════════════════════════════════════════════════

export async function getWalletHistory(walletAddress, limit = 100) {
  if (!walletAddress) return [];
  try {
    const { data, error } = await supabase
      .from('purifications')
      .select('*')
      .eq('wallet_address', walletAddress)
      .is('game_type', null)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('getWalletHistory error:', error);
      return [];
    }

    // Map Supabase rows to the same shape historyManager uses locally
    return (data || []).map(row => ({
      id: `purification_${row.id}`,
      timestamp: new Date(row.created_at).getTime(),
      date: row.created_at,
      objectCount: row.object_count || 0,
      purityEarned: row.purity_earned || 0,
      suiReclaimed: row.sui_reclaimed || 0,
      storageRebateMist: row.storage_rebate_mist || 0,
      gasUsed: row.gas_used || 0,
      transactionHash: row.transaction_hash || null,
      categoryBreakdown: {
        TOXIC: row.category_toxic || 0,
        SPAM: row.category_spam || 0,
        DUST: row.category_dust || 0,
      },
      isToiletMode: row.is_toilet_mode || false,
      isDemoMode: false,
    }));
  } catch (err) {
    console.error('getWalletHistory failed:', err);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ+WRITE: Streak sync
// Saves streak to Supabase and restores on wallet connect
// ═══════════════════════════════════════════════════════════════════════════

export async function saveStreakToSupabase(walletAddress, streakData) {
  if (!walletAddress) return false;
  try {
    const { error } = await supabase
      .from('user_stats')
      .upsert({
        wallet_address: walletAddress,
        current_streak: streakData.currentStreak || 0,
        longest_streak: streakData.longestStreak || 0,
        last_streak_date: streakData.lastPurificationDate || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'wallet_address' });
    if (error) { console.error('saveStreakToSupabase error:', error); return false; }
    return true;
  } catch (err) {
    console.error('saveStreakToSupabase failed:', err);
    return false;
  }
}

export async function getStreakFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('current_streak, longest_streak, last_streak_date')
      .eq('wallet_address', walletAddress)
      .single();
    if (error || !data) return null;
    return {
      currentStreak: data.current_streak || 0,
      longestStreak: data.longest_streak || 0,
      lastPurificationDate: data.last_streak_date || null,
    };
  } catch (err) {
    console.error('getStreakFromSupabase failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// READ+WRITE: Achievements sync
// Saves achievement IDs to Supabase and restores on wallet connect
// ═══════════════════════════════════════════════════════════════════════════

export async function saveAchievementsToSupabase(walletAddress, achievementIds) {
  if (!walletAddress) return false;
  try {
    const { error } = await supabase
      .from('user_stats')
      .upsert({
        wallet_address: walletAddress,
        achievements_unlocked: achievementIds,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'wallet_address' });
    if (error) { console.error('saveAchievementsToSupabase error:', error); return false; }
    return true;
  } catch (err) {
    console.error('saveAchievementsToSupabase failed:', err);
    return false;
  }
}

export async function getAchievementsFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('achievements_unlocked')
      .eq('wallet_address', walletAddress)
      .single();
    if (error || !data) return null;
    return data.achievements_unlocked || [];
  } catch (err) {
    console.error('getAchievementsFromSupabase failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// WHITELIST SYNC — cross-device persistence via user_stats.whitelist_data
// ═══════════════════════════════════════════════════════════════════════════

export async function saveWhitelistToSupabase(walletAddress, whitelist) {
  if (!walletAddress) return false;
  try {
    const { error } = await supabase
      .from('user_stats')
      .upsert({
        wallet_address: walletAddress,
        whitelist_data: whitelist,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'wallet_address' });
    if (error) { console.error('saveWhitelistToSupabase error:', error); return false; }
    return true;
  } catch (err) {
    console.error('saveWhitelistToSupabase failed:', err);
    return false;
  }
}

export async function getWhitelistFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('whitelist_data')
      .eq('wallet_address', walletAddress)
      .single();
    if (error || !data) return null;
    return data.whitelist_data || {};
  } catch (err) {
    console.error('getWhitelistFromSupabase failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// QUARANTINE SYNC — cross-device persistence via user_stats.quarantine_data
// Stores full object array (not just IDs) so we can display type/id in modal
// ═══════════════════════════════════════════════════════════════════════════

export async function saveQuarantineToSupabase(walletAddress, quarantineArray) {
  if (!walletAddress) return false;
  try {
    const { error } = await supabase
      .from('user_stats')
      .upsert({
        wallet_address: walletAddress,
        quarantine_data: quarantineArray,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'wallet_address' });
    if (error) { console.error('saveQuarantineToSupabase error:', error); return false; }
    return true;
  } catch (err) {
    console.error('saveQuarantineToSupabase failed:', err);
    return false;
  }
}

export async function getQuarantineFromSupabase(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data, error } = await supabase
      .from('user_stats')
      .select('quarantine_data')
      .eq('wallet_address', walletAddress)
      .single();
    if (error || !data) return null;
    return data.quarantine_data || [];
  } catch (err) {
    console.error('getQuarantineFromSupabase failed:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// REFERRAL SYSTEM — Supabase helpers
// Used by referralManager.js for all referral DB operations
// ═══════════════════════════════════════════════════════════════════════════

export { supabase };
